/**
 * Platform Analytics range selector. Matches the backend's unified date-range query
 * shape defined in services/analytics/query_service.py:
 *
 *     ?range=today|7d|14d|30d|90d|mtd|qtd|ytd|custom&start=YYYY-MM-DD&end=YYYY-MM-DD
 *
 * Use alongside React Query — put `queryString` into the queryKey so a range change
 * triggers a refetch.
 */
import { useCallback, useMemo, useState } from 'react';
import { format, startOfDay, subDays } from 'date-fns';

export type RangeKey =
  | 'today'
  | '7d'
  | '14d'
  | '30d'
  | '90d'
  | 'mtd'
  | 'qtd'
  | 'ytd'
  | 'custom';

const PRESET_LABELS: Record<Exclude<RangeKey, 'custom'>, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '14d': 'Last 14 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  mtd: 'Month to date',
  qtd: 'Quarter to date',
  ytd: 'Year to date',
};

export const PRESET_ORDER: (keyof typeof PRESET_LABELS)[] = [
  'today',
  '7d',
  '30d',
  '90d',
  'mtd',
  'qtd',
  'ytd',
];

export function presetLabel(key: Exclude<RangeKey, 'custom'>): string {
  return PRESET_LABELS[key];
}

function rangeToLocalBounds(key: RangeKey, today: Date): [Date, Date] {
  const t = startOfDay(today);
  switch (key) {
    case 'today':
      return [t, t];
    case '7d':
      return [subDays(t, 6), t];
    case '14d':
      return [subDays(t, 13), t];
    case '30d':
      return [subDays(t, 29), t];
    case '90d':
      return [subDays(t, 89), t];
    case 'mtd':
      return [new Date(t.getFullYear(), t.getMonth(), 1), t];
    case 'qtd': {
      const qStartMonth = Math.floor(t.getMonth() / 3) * 3;
      return [new Date(t.getFullYear(), qStartMonth, 1), t];
    }
    case 'ytd':
      return [new Date(t.getFullYear(), 0, 1), t];
    case 'custom':
      return [t, t];
  }
}

export interface AnalyticsRange {
  rangeKey: RangeKey;
  start: Date;
  end: Date;
  /** Query string fragment ready to append, without leading `?` or `&`. */
  queryString: string;
  label: string;
  setRange: (key: RangeKey) => void;
  setCustomRange: (start: Date, end: Date) => void;
}

export function useAnalyticsRange(defaultKey: RangeKey = '30d'): AnalyticsRange {
  const [rangeKey, setRangeKey] = useState<RangeKey>(defaultKey);
  const [customBounds, setCustomBounds] = useState<[Date, Date] | null>(null);

  const [start, end] = useMemo<[Date, Date]>(() => {
    if (rangeKey === 'custom' && customBounds) return customBounds;
    return rangeToLocalBounds(rangeKey, new Date());
  }, [rangeKey, customBounds]);

  const queryString = useMemo(() => {
    if (rangeKey === 'custom') {
      return `range=custom&start=${format(start, 'yyyy-MM-dd')}&end=${format(end, 'yyyy-MM-dd')}`;
    }
    return `range=${rangeKey}`;
  }, [rangeKey, start, end]);

  const label =
    rangeKey === 'custom'
      ? `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`
      : PRESET_LABELS[rangeKey];

  const setRange = useCallback((key: RangeKey) => {
    setRangeKey(key);
    if (key !== 'custom') setCustomBounds(null);
  }, []);

  const setCustomRange = useCallback((s: Date, e: Date) => {
    setCustomBounds([startOfDay(s), startOfDay(e)]);
    setRangeKey('custom');
  }, []);

  return { rangeKey, start, end, queryString, label, setRange, setCustomRange };
}
